import axios from 'axios';

/**
 * Schemes V2 API Service
 * 
 * Updated to use direct endpoint URLs without .htaccess routing:
 * 
 * Direct Endpoints:
 * - GET  /v2.0.0/schemes/index.php                    - Get all schemes
 * - GET  /v2.0.0/schemes/index.php?schemeId={id}      - Get specific scheme
 * - POST /v2.0.0/schemes/index.php/{schemeId}/enroll  - Enroll in scheme
 * - GET  /v2.0.0/user/schemes/index.php?user_id={id}  - Get user's schemes
 * - GET  /v2.0.0/user/schemes/index.php/{id}?user_id={id} - Get enrollment details
 * - GET  /v2.0.0/user/schemes/benefits.php?product={id}&user_id={id} - Get scheme benefits
 * - POST /v2.0.0/schemes/payment/create_order.php     - Create payment order
 * - POST /v2.0.0/schemes/payment/verify.php           - Verify payment
 * - POST /v2.0.0/schemes/payment/process.php          - Process payment
 * - GET  /v2.0.0/rates/current.php                    - Get current rates
 * - POST /v2.0.0/rates/current.php                    - Update rates
 */

// Base API configuration
const BASE_URL = process.env.REACT_APP_API_URL;
const LOCALHOST_URL = 'http://localhost:5055';
const API_VERSION = 'v2.0.0';

// Create axios instance with default config
const apiClient = axios.create({
  baseURL: `${BASE_URL}${API_VERSION}`,
  timeout: 1000000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Create localhost axios instance for detailed data
const localhostClient = axios.create({
  baseURL: `${LOCALHOST_URL}${API_VERSION}`,
  timeout: 1000000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for error handling
apiClient.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    if (error.response?.status === 401) {
      // Handle unauthorized access
      localStorage.removeItem('token');
      localStorage.removeItem('user_id');
      window.location.href = '/signin';
    }
    return Promise.reject(error);
  }
);

// Schemes V2 API Service
export const schemesV2Api = {
  // Get all schemes
  getAllSchemes: async () => {
    try {
      const response = await apiClient.get('/schemes/index.php');
      return response.data;
    } catch (error) {
      throw new Error(`Failed to fetch schemes: ${error.response?.data?.error?.message || error.message}`);
    }
  },

  // Get specific scheme by ID
  getSchemeById: async (schemeId) => {
    try {
      // Use localhost client for detailed scheme data with key terms
      const response = await localhostClient.get(`/schemes/${schemeId}`);
      return response.data;
    } catch (error) {
      console.warn('Localhost API failed, trying production API:', error.message);
      try {
        // Fallback to production API
        const response = await apiClient.get(`/schemes/index.php?schemeId=${schemeId}`);
        return response.data;
      } catch (fallbackError) {
        throw new Error(`Failed to fetch scheme: ${fallbackError.response?.data?.error?.message || fallbackError.message}`);
      }
    }
  },

  // Enroll user in a scheme
  enrollInScheme: async (schemeId, enrollmentData) => {
    try {
      const response = await apiClient.post(`/schemes/index.php/${schemeId}/enroll`, enrollmentData);
      return response.data;
    } catch (error) {
      throw new Error(`Failed to enroll in scheme: ${error.response?.data?.error?.message || error.message}`);
    }
  },

  // Get user's schemes
  getUserSchemes: async (userId) => {
    try {
      const response = await apiClient.get(`/user/schemes/index.php?user_id=${userId}`);
      return response.data;
    } catch (error) {
      throw new Error(`Failed to fetch user schemes: ${error.response?.data?.error?.message || error.message}`);
    }
  },

  // Get specific enrollment details
  getEnrollmentDetails: async (enrollmentId, userId) => {
    try {
      const response = await apiClient.get(`/user/schemes/index.php/${enrollmentId}?user_id=${userId}`);
      return response.data;
    } catch (error) {
      throw new Error(`Failed to fetch enrollment details: ${error.response?.data?.error?.message || error.message}`);
    }
  },

  // Get scheme benefits for a product
  getSchemeBenefits: async (productId, userId = null) => {
    try {
      const params = new URLSearchParams({ product: productId });
      if (userId) {
        params.append('user_id', userId);
      }
      const response = await apiClient.get(`/user/schemes/benefits.php?${params.toString()}`);
      return response.data;
    } catch (error) {
      throw new Error(`Failed to fetch scheme benefits: ${error.response?.data?.error?.message || error.message}`);
    }
  },

  // Create Razorpay order for payment
  createRazorpayOrder: async (orderData) => {
    try {
      const response = await apiClient.post('/schemes/payment/create_order.php', orderData);
      return response.data;
    } catch (error) {
      throw new Error(`Failed to create Razorpay order: ${error.response?.data?.error?.message || error.message}`);
    }
  },

  // Create first installment order (New API)
  createFirstInstallmentOrder: async (enrollmentId, orderData) => {
    try {
      const response = await apiClient.post('/schemes/payment/create_order.php', {
        ...orderData,
        enrollmentId: enrollmentId,
        installmentNumber: 1
      });
      return response.data;
    } catch (error) {
      throw new Error(`Failed to create first installment order: ${error.response?.data?.error?.message || error.message}`);
    }
  },

  // Create order for any installment (Updated API)
  createInstallmentOrder: async (enrollmentId, installmentNumber, orderData) => {
    try {
      const response = await apiClient.post('/schemes/payment/create_order.php', {
        ...orderData,
        enrollmentId: enrollmentId,
        installmentNumber: installmentNumber
      });
      return response.data;
    } catch (error) {
      throw new Error(`Failed to create installment order: ${error.response?.data?.error?.message || error.message}`);
    }
  },

  // Verify Razorpay payment
  verifyPayment: async (verificationData) => {
    try {
      const response = await apiClient.post('/schemes/payment/verify.php', verificationData);
      return response.data;
    } catch (error) {
      throw new Error(`Failed to verify payment: ${error.response?.data?.error?.message || error.message}`);
    }
  },

  // Process payment (legacy method for backward compatibility)
  processPayment: async (paymentData) => {
    try {
      const response = await apiClient.post('/schemes/payment/process.php', paymentData);
      return response.data;
    } catch (error) {
      throw new Error(`Failed to process payment: ${error.response?.data?.error?.message || error.message}`);
    }
  },

  // Create Razorpay subscription
  createSubscription: async (subscriptionData) => {
    try {
      const response = await apiClient.post('/schemes/subscription/create.php', subscriptionData);
      return response.data;
    } catch (error) {
      throw new Error(`Failed to create subscription: ${error.response?.data?.error?.message || error.message}`);
    }
  },

  // Cancel Razorpay subscription (legacy method)
  cancelSubscriptionLegacy: async (subscriptionId) => {
    try {
      const response = await apiClient.post('/schemes/subscription/cancel.php', { subscriptionId });
      return response.data;
    } catch (error) {
      throw new Error(`Failed to cancel subscription: ${error.response?.data?.error?.message || error.message}`);
    }
  },

  // Verify subscription payment (V1 style activation)
  verifySubscriptionPayment: async (verificationData) => {
    try {
      const response = await apiClient.post('/schemes/subscription/verify.php', verificationData);
      return response.data;
    } catch (error) {
      throw new Error(`Failed to verify subscription payment: ${error.response?.data?.error?.message || error.message}`);
    }
  },

  // Get current gold/silver rates
  getCurrentRates: async () => {
    try {
      const response = await apiClient.get('/rates/current.php');
      return response.data;
    } catch (error) {
      throw new Error(`Failed to fetch current rates: ${error.response?.data?.error?.message || error.message}`);
    }
  },

  // Update rates (admin only)
  updateRates: async (ratesData) => {
    try {
      const response = await apiClient.post('/rates/current.php', ratesData);
      return response.data;
    } catch (error) {
      throw new Error(`Failed to update rates: ${error.response?.data?.error?.message || error.message}`);
    }
  },

  // Get subscription details
  getSubscriptionDetails: async (enrollmentId) => {
    try {
      const response = await apiClient.get(`/schemes/index.php/${enrollmentId}/subscription`);
      return response.data;
    } catch (error) {
      throw new Error(`Failed to fetch subscription details: ${error.response?.data?.error?.message || error.message}`);
    }
  },

  // Cancel subscription
  cancelSubscription: async (subscriptionId) => {
    try {
      const response = await apiClient.post('/schemes/subscription/cancel.php', { subscriptionId });
      return response.data;
    } catch (error) {
      throw new Error(`Failed to cancel subscription: ${error.response?.data?.error?.message || error.message}`);
    }
  },

  // Get user dashboard data
  getUserDashboard: async (userId) => {
    try {
      const response = await apiClient.get(`/user/schemes/index.php?user_id=${userId}`);
      return response.data;
    } catch (error) {
      throw new Error(`Failed to fetch user dashboard: ${error.response?.data?.error?.message || error.message}`);
    }
  },
};

// Utility functions
export const schemesV2Utils = {
  // Format currency
  formatCurrency: (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format(amount);
  },

  // Format date
  formatDate: (dateString) => {
    return new Date(dateString).toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  },

  // Calculate progress percentage
  calculateProgress: (paid, total) => {
    return total > 0 ? Math.round((paid / total) * 100) : 0;
  },

  // Calculate gold grams from amount
  calculateGoldGrams: (amount, goldRate) => {
    return goldRate > 0 ? amount / goldRate : 0;
  },

  // Validate enrollment data
  validateEnrollmentData: (data, scheme) => {
    const errors = [];
    
    if (!data.instalmentAmount || data.instalmentAmount <= 0) {
      errors.push('Instalment amount is required and must be greater than 0');
    }
    
    if (scheme.minAmount && data.instalmentAmount < scheme.minAmount) {
      errors.push(`Minimum instalment amount is ₹${scheme.minAmount}`);
    }
    
    if (scheme.maxAmount && data.instalmentAmount > scheme.maxAmount) {
      errors.push(`Maximum instalment amount is ₹${scheme.maxAmount}`);
    }
    
    if (!data.userId) {
      errors.push('User ID is required');
    }
    
    if (!data.startDate) {
      errors.push('Start date is required');
    }
    
    return errors;
  },

  // Validate payment data
  validatePaymentData: (data) => {
    const errors = [];
    
    if (!data.enrollmentId) {
      errors.push('Enrollment ID is required');
    }
    
    if (!data.installmentNumber || data.installmentNumber <= 0) {
      errors.push('Installment number is required and must be greater than 0');
    }
    
    if (!data.amount || data.amount <= 0) {
      errors.push('Payment amount is required and must be greater than 0');
    }
    
    if (!data.paymentMethod) {
      errors.push('Payment method is required');
    }
    
    return errors;
  },

  // Load Razorpay script dynamically
  loadRazorpayScript: () => {
    return new Promise((resolve) => {
      if (window.Razorpay) {
        resolve(true);
        return;
      }
      const script = document.createElement('script');
      script.src = 'https://checkout.razorpay.com/v1/checkout.js';
      script.onload = () => resolve(true);
      script.onerror = () => resolve(false);
      document.body.appendChild(script);
    });
  },

  // Initialize Razorpay payment
  initializeRazorpayPayment: (options) => {
    if (!window.Razorpay) {
      throw new Error('Razorpay SDK not loaded');
    }
    return new window.Razorpay(options);
  },

  // Generate unique receipt ID
  generateReceiptId: (enrollmentId, installmentNumber) => {
    const random = Math.random().toString(36).substring(2, 8);
    return `scheme_enrollment_${enrollmentId}_installment_${installmentNumber}_${random}`;
  },

  // Validate Razorpay response
  validateRazorpayResponse: (response) => {
    const requiredFields = ['razorpay_payment_id', 'razorpay_order_id', 'razorpay_signature'];
    const missingFields = requiredFields.filter(field => !response[field]);
    
    if (missingFields.length > 0) {
      throw new Error(`Missing required Razorpay response fields: ${missingFields.join(', ')}`);
    }
    
    return true;
  },
};

export default schemesV2Api;
